import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Edit2, Save, X, Plus, Trash2, Settings, BarChart3 } from 'lucide-react';
import './PriceList.css';
import priceService from '../../services/priceService';

const PriceList = () => {
  const FIXED_CATEGORIES = ['Paper', 'Metal', 'Plastic', 'E-Waste', 'Glass', 'Textile'];
  const [priceData, setPriceData] = useState(
    FIXED_CATEGORIES.reduce((acc, c) => ({ ...acc, [c]: [] }), {})
  );
  const [loading, setLoading] = useState(false);
  
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', price: '', unit: '', description: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItemCategory, setNewItemCategory] = useState('Paper');
  const [saving, setSaving] = useState(false);

  // Fetch all items on mount
  useEffect(() => {
    const fetchItems = async () => {
      try {
        setLoading(true);
        const result = await priceService.getScrapItems();
        const items = Array.isArray(result) ? result : result?.data || [];
        const grouped = items.reduce((acc, it) => {
          const cat = it.category || 'Other';
          if (!acc[cat]) acc[cat] = [];
          acc[cat].push({
            id: it.id,
            name: it.name,
            price: it.price,
            unit: it.unit || 'kg',
            description: it.description || '',
          });
          return acc;
        }, {});
        const normalized = FIXED_CATEGORIES.reduce((acc, c) => {
          acc[c] = grouped[c] || [];
          return acc;
        }, {});
        setPriceData(normalized);
      } catch (e) {
        console.error('Failed to load items', e);
        alert(e.message || 'Failed to load items');
      } finally {
        setLoading(false);
      }
    };
    fetchItems();
  }, []);

  const handleEdit = (item, category) => {
    setEditingItem({ ...item, category });
    setEditForm({
      name: item.name,
      price: item.price,
      unit: item.unit,
      description: item.description
    });
  };

  const handleSave = async () => {
    const { category, id } = editingItem;
    const payload = {
      category,
      name: editForm.name,
      unit: editForm.unit || 'kg',
      price: parseFloat(editForm.price),
      description: editForm.description,
      active: true,
    };
    try {
      setSaving(true);
      const updated = await priceService.updateScrapItem(id, payload);

      const updatedItem = {
        id: updated?.id || id,
        name: updated?.name || payload.name,
        price: updated?.price ?? payload.price,
        unit: updated?.unit || payload.unit,
        description: updated?.description || payload.description,
      };

      setPriceData(prev => ({
        ...prev,
        [category]: prev[category].map(item => 
          item.id === id ? updatedItem : item
        )
      }));

      setEditingItem(null);
      setEditForm({ name: '', price: '', unit: '', description: '' });
    } catch (e) {
      console.error('Failed to update item', e);
      alert(e.message || 'Failed to update item');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingItem(null);
    setEditForm({ name: '', price: '', unit: '', description: '' });
  };

  const handleAddItem = async () => {
    try {
      setSaving(true);
      const payload = {
        category: newItemCategory,
        name: editForm.name,
        unit: editForm.unit || 'kg',
        price: parseFloat(editForm.price),
        description: editForm.description,
        active: true,
      };

      const created = await priceService.addScrapItem(payload);

      const newItem = {
        id: created?.id || Date.now(),
        name: created?.name || payload.name,
        price: created?.price ?? payload.price,
        unit: created?.unit || payload.unit,
        description: created?.description || payload.description,
      };

      setPriceData(prev => ({
        ...prev,
        [newItemCategory]: [...prev[newItemCategory], newItem]
      }));

      setShowAddForm(false);
      setEditForm({ name: '', price: '', unit: '', description: '' });
    } catch (e) {
      console.error('Failed to add item', e);
      alert(e.message || 'Failed to add item');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (itemId, category) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      setSaving(true);
      try {
        await priceService.deleteScrapItem(itemId);
        setPriceData(prev => ({
          ...prev,
          [category]: prev[category].filter(item => item.id !== itemId)
        }));
      } catch (e) {
        console.error('Failed to delete item', e);
        alert(e.message || 'Failed to delete item');
      } finally {
        setSaving(false);
      }
    }
  };

  const categories = FIXED_CATEGORIES;

  return (
    <div className="price-list-container">
      <div className="price-list-header">
        <div className="header-top">
          <h1>Scrap Price List Management</h1>
          <div className="header-nav">
            <Link to="/dashboard" className="nav-link">
              <BarChart3 size={20} />
              Bookings
            </Link>
            <Link to="/prices" className="nav-link active">
              <Settings size={20} />
              Price Management
            </Link>
          </div>
        </div>
        <p>Manage current market rates for different types of scrap materials</p>
        <div className="header-actions">
          <button 
            className="add-item-btn"
            onClick={() => setShowAddForm(true)}
            disabled={saving}
          >
            <Plus size={20} />
            Add New Item
          </button>
        </div>
      </div>

      {showAddForm && (
        <div className="add-item-form">
          <h3>Add New Price Item</h3>
          <div className="form-row">
            <select 
              value={newItemCategory} 
              onChange={(e) => setNewItemCategory(e.target.value)}
            >
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Item name"
              value={editForm.name}
              onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
            />
            <input
              type="number"
              placeholder="Price"
              value={editForm.price}
              onChange={(e) => setEditForm(prev => ({ ...prev, price: e.target.value }))}
            />
            <select
              value={editForm.unit}
              onChange={(e) => setEditForm(prev => ({ ...prev, unit: e.target.value }))}
            >
              <option value="">Select unit</option>
              <option value="kg">kg</option>
              <option value="piece">piece</option>
            </select>
          </div>
          <input
            type="text"
            placeholder="Description"
            value={editForm.description}
            onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
            className="description-input"
          />
          <div className="form-actions">
            <button onClick={handleAddItem} className="save-btn" disabled={saving}>
              <Save size={16} />
              {saving ? 'Adding...' : 'Add Item'}
            </button>
            <button onClick={() => setShowAddForm(false)} className="cancel-btn" disabled={saving}>
              <X size={16} />
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="price-categories">
        {loading && <p>Loading items...</p>}
        {!loading && categories.map(category => (
          <div key={category} className="price-category">
            <h2>{category}</h2>
            <div className="price-items">
              {priceData[category].map(item => (
                <div key={item.id} className="price-item">
                  {editingItem && editingItem.id === item.id ? (
                    <div className="edit-form">
                      <div className="form-row">
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                        />
                        <input
                          type="number"
                          value={editForm.price}
                          onChange={(e) => setEditForm(prev => ({ ...prev, price: e.target.value }))}
                        />
                        <select
                          value={editForm.unit}
                          onChange={(e) => setEditForm(prev => ({ ...prev, unit: e.target.value }))}
                        >
                          <option value="kg">kg</option>
                          <option value="piece">piece</option>
                        </select>
                      </div>
                      <input
                        type="text"
                        value={editForm.description}
                        onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                        className="description-input"
                      />
                      <div className="form-actions">
                        <button onClick={handleSave} className="save-btn" disabled={saving}>
                          <Save size={16} />
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button onClick={handleCancel} className="cancel-btn" disabled={saving}>
                          <X size={16} />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="item-content">
                      <div className="item-info">
                        <h3>{item.name}</h3>
                        <p className="price">{item.price} / {item.unit}</p>
                        <p className="description">{item.description}</p>
                      </div>
                      <div className="item-actions">
                        <button 
                          onClick={() => handleEdit(item, category)}
                          className="edit-btn"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button 
                          onClick={() => handleDelete(item.id, category)}
                          className="delete-btn"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="price-note">
        <p>
          <strong>Note:</strong> Prices are subject to market fluctuations and may vary based on quality and quantity. 
          Final prices will be determined during pickup after inspection.
        </p>
      </div>
    </div>
  );
};

export default PriceList;

